// src/components/modals/NFTDetailModal.js
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { ethers } from 'ethers';
import axios from 'axios';
import { parseTxError } from '../../utils/parseTxError';
import { addressToSogniUsername } from '../../utils/addressToSogniUsername';
import { Link } from 'react-router-dom';
import { getMainnetProvider } from '../../utils/providers';
import { getApiBaseUrl } from '../../utils/apiConfig';
import moment from 'moment';

const PROSPECT_IMG =
  'https://nft.sogni.ai/big/badge-fast-worker-prospect/001.jpg';

/**
 *
 * @param {Object} props
 * @param {boolean} props.show - whether the modal is visible
 * @param {function} props.handleClose - function to close/hide the modal
 * @param {Object}   props.selectedNFT - the NFT object with tokenId, minted, owner, etc.
 * @param {string}   props.baseURI
 * @param {boolean}  props.isWalletConnected - e.g. whether Ethers wallet is connected
 * @param {string}   props.account - current wallet address
 * @param {string}   props.transferAddress
 * @param {function} props.setTransferAddress
 * @param {boolean}  props.transferring
 * @param {function} props.handleTransfer
 * @param {string}   props.transferError
 * @param {string}   props.transferSuccess
 * @param {boolean}  props.minting
 * @param {function} props.handleMint - the final call that does "getMintSignature" + TX
 * @param {string}   props.mintError
 * @param {string}   props.mintSuccess
 * @param {Object}   props.publicContract - read-only contract for display
 * @param {string}   props.mintPrice - e.g. "0.0"
 * @param {number}   props.mintLimit - highest selectable token id
 *
 * // Sogni-related
 * @param {boolean}  props.isSogniLoggedIn   - must be true after user logs in
 * @param {function} props.handleSogniLogin  - triggers your Sogni login flow or opens login modal
 * (token-based auth removed; using cookie-based auth)
 * @param {Object}   props.sogniClient       - optional
 *
 * // Turnstile
 * @param {string}   props.turnstileToken
 * @param {function} props.setTurnstileToken
 */
const NFTDetailModal = ({
  show,
  handleClose,
  selectedNFT,
  baseURI,
  isWalletConnected,
  account,
  transferAddress,
  setTransferAddress,
  transferring,
  handleTransfer,
  transferError,
  transferSuccess,
  minting,
  handleMint,
  mintError,
  mintSuccess,
  publicContract,
  etherlinkPublicContract,
  mintPrice,
  mintLimit,

  // Sogni props
  isSogniLoggedIn,
  handleSogniLogin,
  sogniClient,
  sogniUsername,

  // Turnstile token
  turnstileToken,
  setTurnstileToken
}) => {
  const imageClassName = mintSuccess ? 'just-minted' : '';
  const [ownerUsername, setOwnerUsername] = useState(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  // For "View API key" panel
  const [showApiKeyPanel, setShowApiKeyPanel] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyError, setApiKeyError] = useState(null);
  const [turnstileError, setTurnstileError] = useState(null);
  const turnstileWidgetIdRef = useRef(null);

  // For signature verification (scanned NFTs)
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [verificationPersisted, setVerificationPersisted] = useState(false);
  const [isAlreadyVerified, setIsAlreadyVerified] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);

  // Socket status for NFT (Explorer click)
  const [nftStatus, setNftStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);
  // Lifetime analytics
  const [lifetimeStats, setLifetimeStats] = useState(null);
  const [lifetimeLoading, setLifetimeLoading] = useState(false);

  // Blacklist state
  const [isBlackListed, setIsBlackListed] = useState(false);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [blacklistImageUrl, setBlacklistImageUrl] = useState(null);

  // Locked/bridged state (Base only — token held by bridge adapter)
  const [isLocked, setIsLocked] = useState(false);

  // A token is cleared after every mint attempt because Turnstile tokens are
  // single-use. Reset the existing widget so the next attempt gets a fresh one.
  useEffect(() => {
    if (
      !turnstileToken &&
      turnstileWidgetIdRef.current !== null &&
      window.turnstile
    ) {
      try {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      } catch (error) {
        console.warn('Unable to reset Turnstile widget:', error);
        turnstileWidgetIdRef.current = null;
      }
    }
  }, [turnstileToken]);

  useEffect(() => () => {
    if (turnstileWidgetIdRef.current !== null && window.turnstile?.remove) {
      try {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      } catch (error) {
        console.warn('Unable to remove Turnstile widget:', error);
      }
    }
    turnstileWidgetIdRef.current = null;
  }, []);

  // Determine viewer address from external wallet or Sogni session wallet
  const viewerAddressLc = (
    account || sogniClient?.account?.currentAccount?.walletAddress || ''
  ).toLowerCase();
  const ownerAddressLc = selectedNFT?.owner ? selectedNFT.owner.toLowerCase() : '';

  // Reset video loading state when modal opens with a new NFT
  useEffect(() => {
    if (show && selectedNFT?.isScannedNFT) {
      if (selectedNFT?.animationUrl) {
        setVideoLoading(true);
        setVideoError(false);
      }
      // Reset verification states for new NFT
      setIsVerified(false);
      setIsVerifying(false);
      setVerificationError(null);
      setVerificationPersisted(false);
      setIsAlreadyVerified(false);
      
      // Check if NFT is already verified
      const checkVerificationStatus = async () => {
        if (!account || !selectedNFT?.contractAddress || !selectedNFT?.tokenId) {
          return;
        }
        
        setCheckingVerification(true);
        try {
          const baseUrl = getApiBaseUrl();
          if (!baseUrl) {
            console.warn('API base URL not configured');
            return;
          }
          
          const response = await axios.get(
            `${baseUrl}/nfts/verified-owner?wallet=${account.toLowerCase()}`
          );
          
          if (response.data?.success && response.data?.verifiedNFTs) {
            const contractAddr = selectedNFT.contractAddress.toLowerCase();
            const verifiedNFTs = response.data.verifiedNFTs;
            
            // Check if this contract address exists in verified NFTs
            if (verifiedNFTs[contractAddr]) {
              // Check if this specific tokenId is in the array
              const tokenIdNum = parseInt(selectedNFT.tokenId, 10);
              if (verifiedNFTs[contractAddr].includes(tokenIdNum)) {
                setIsAlreadyVerified(true);
                setIsVerified(true);
              }
            }
          }
        } catch (error) {
          console.error('Error checking verification status:', error);
          // Silently fail - we'll just show the verify button
        } finally {
          setCheckingVerification(false);
        }
      };
      
      checkVerificationStatus();
    }
  }, [show, selectedNFT, account]);

  // Fetch real-time worker status from socket.sogni.ai when modal opens
  useEffect(() => {
    const fetchStatus = async () => {
      if (!show || !selectedNFT?.tokenId) return;
      setStatusError(null);
      setStatusLoading(true);
      try {
        const url = `https://socket.sogni.ai/api/v1/client/nft/${selectedNFT.tokenId}/status`;
        const resp = await axios.get(url, { timeout: 8000 });
        setNftStatus(resp?.data || null);
      } catch (err) {
        // Silent failure: keep default "Never"
        setNftStatus(null);
        setStatusError(null);
      } finally {
        setStatusLoading(false);
      }
    };
    fetchStatus();
  }, [show, selectedNFT?.tokenId]);

  // Fetch lifetime analytics for the NFT
  useEffect(() => {
    const fetchLifetime = async () => {
      if (!show || !selectedNFT?.tokenId) return;
      setLifetimeLoading(true);
      try {
        const url = `https://api.sogni.ai/v1/analytics/lifetime/nft/${selectedNFT.tokenId}`;
        const resp = await axios.get(url, { timeout: 10000 });
        const data = resp?.data?.data || null;
        setLifetimeStats(data);
      } catch (_) {
        setLifetimeStats(null);
      } finally {
        setLifetimeLoading(false);
      }
    };
    fetchLifetime();
  }, [show, selectedNFT?.tokenId]);

  // Fetch blacklist + locked/bridged status — use Etherlink contract for Etherlink NFTs, Base contract otherwise
  useEffect(() => {
    const fetchNFTStatus = async () => {
      if (!show || !selectedNFT?.tokenId || selectedNFT?.isScannedNFT) return;

      const isBase = selectedNFT?.chain !== 'Etherlink';
      const nftContract = isBase ? publicContract : etherlinkPublicContract;
      if (!nftContract) return;

      setBlacklistLoading(true);
      setIsBlackListed(false);
      setBlacklistImageUrl(null);
      setIsLocked(false);

      try {
        // Check blacklist status
        const blacklisted = await nftContract.isBlacklisted(selectedNFT.tokenId);
        setIsBlackListed(blacklisted);

        // If blacklisted, fetch the status image from metadata API
        if (blacklisted) {
          try {
            const baseUrl = getApiBaseUrl();
            if (baseUrl) {
              const resp = await axios.get(`${baseUrl}/metadata/${selectedNFT.tokenId}`, { timeout: 8000 });
              if (resp.data?.image) {
                setBlacklistImageUrl(resp.data.image);
              }
            }
          } catch (imgErr) {
            console.error('Error fetching metadata image for blacklisted NFT:', imgErr);
          }
        }

        // Check locked/bridged status (Base only — Etherlink NFTs can't be locked)
        if (!blacklisted && isBase) {
          try {
            const bridged = await nftContract.isBridged(selectedNFT.tokenId);
            setIsLocked(bridged);
          } catch (bridgeErr) {
            // isBridged may not exist on older contract versions — silently ignore
            console.error('Error checking bridge status:', bridgeErr);
          }
        }
      } catch (err) {
        console.error('Error checking NFT status:', err);
        setIsBlackListed(false);
        setIsLocked(false);
      } finally {
        setBlacklistLoading(false);
      }
    };

    fetchNFTStatus();
  }, [show, selectedNFT?.tokenId, selectedNFT?.chain, selectedNFT?.isScannedNFT, publicContract, etherlinkPublicContract]);

  // Attempt to fetch Sogni username for the NFT owner (if minted).
  useEffect(() => {
    async function fetchOwnerUsername() {
      if (!selectedNFT?.minted || !selectedNFT?.owner) return;
      const rawOwner = selectedNFT.owner.trim().toLowerCase();
      const rawAccount = account?.trim().toLowerCase();
      if (!rawOwner) return;

      if (rawAccount && rawOwner === rawAccount) {
        setOwnerUsername('You');
        return;
      }
      if (ethers.utils.isAddress(rawOwner)) {
        const user = await addressToSogniUsername(rawOwner);
        setOwnerUsername(user || null);
      }
    }
    fetchOwnerUsername();
  }, [selectedNFT, account]);

  /** Return something like "username (0xAbCd...1234)" or "You (0xAbCd...)" */
  const getOwnerLabelFormatted = () => {
    if (!selectedNFT?.owner) return null;
    const rawOwner = selectedNFT.owner.trim();
    const rawAccount = account ? account.trim() : '';

    const explorerBase = selectedNFT?.chain === 'Etherlink'
      ? 'https://explorer.etherlink.com/address'
      : 'https://basescan.org/address';

    // If viewer is the owner
    if (rawAccount.toLowerCase() === rawOwner.toLowerCase()) {
      return (
        <>
          You (
          <a
            href={`${explorerBase}/${account}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#39ff14' }}
          >
            {truncateAddress(rawOwner)}
          </a>
          )
        </>
      );
    }

    // If we have a Sogni username
    if (ownerUsername) {
      return (
        <>
          {ownerUsername} (
          <a
            href={`${explorerBase}/${rawOwner}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#39ff14' }}
          >
            {truncateAddress(rawOwner)}
          </a>
          )
        </>
      );
    }

    // Otherwise, link the address
    return (
      <>
        <a
          href={`${explorerBase}/${rawOwner}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#39ff14' }}
        >
          {truncateAddress(rawOwner)}
        </a>
      </>
    );
  };

  const truncateAddress = (addr) => {
    if (!addr) return '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };

  // If user typed Sogni username or .eth or just a 0x address for transferring
  const resolveRecipientAddress = async () => {
    let recipient = transferAddress.trim();
    if (!recipient) return '';

    if (ethers.utils.isAddress(recipient)) {
      return recipient;
    }

    // If ends with .eth => attempt ENS
    if (recipient.toLowerCase().endsWith('.eth')) {
      try {
        const resolvedENS = await getMainnetProvider().resolveName(recipient);
        if (resolvedENS) {
          return resolvedENS;
        }
      } catch (ensErr) {
        console.warn('ENS lookup failed:', ensErr);
      }
    }

    // Try Sogni username => https://api.sogni.ai/v1/account/username-to-address?username=***
    try {
      const resp = await axios.get(
        `https://api.sogni.ai/v1/account/username-to-address?username=${recipient}`
      );
      if (resp.data?.status === 'success' && resp.data.data?.address) {
        const possibleAddr = resp.data.data.address;
        if (ethers.utils.isAddress(possibleAddr)) {
          return possibleAddr;
        }
      }
    } catch (err) {
      console.warn('Sogni username lookup failed:', err);
    }

    // No luck
    return '';
  };

  const handleTransferClick = async () => {
    const resolved = await resolveRecipientAddress();
    if (!resolved) {
      alert('Invalid recipient. Provide a valid address, ENS, or Sogni username.');
      return;
    }
    handleTransfer(resolved);
  };

  // parseTxError convenience
  const shortTransferError = transferError ? parseTxError({ message: transferError }) : null;
  const shortMintError = mintError ? parseTxError({ message: mintError }) : null;

  // "View API key" link / panel
  const handleViewApiKey = async (e) => {
    e.preventDefault();
    setApiKeyError(null);

    try {
      const resp = await axios.post(
        'https://api.sogni.ai/v1/account/api-key',
        {},
        { withCredentials: true }
      );
      if (resp.data?.status === 'success' && resp.data.data?.apiKey) {
        setApiKey(resp.data.data.apiKey);
        setShowApiKeyPanel(true);
      } else {
        setApiKeyError('Failed to retrieve API key.');
      }
    } catch (err) {
      console.error('Error fetching API key:', err);
      setApiKeyError('Error fetching API key.');
    }
  };

  const handleResetApiKey = async () => {
    setApiKeyError(null);
    try {
      // "?refresh=1"
      const resp = await axios.post(
        'https://api.sogni.ai/v1/account/api-key?refresh=1',
        {},
        { withCredentials: true }
      );
      if (resp.data?.status === 'success' && resp.data.data?.apiKey) {
        setApiKey(resp.data.data.apiKey);
      } else {
        setApiKeyError('Failed to retrieve new API key.');
      }
    } catch (err) {
      console.error('Error refreshing API key:', err);
      setApiKeyError('Error refreshing API key.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch((err) => {
      console.error('Failed to copy text:', err);
    });
  };

  // Helper for "X days/hours/minutes ago"
  const calculateRelativeTime = (timestamp) => {
    if (!timestamp || timestamp === 'undefined' || timestamp === 'null' || timestamp === null) return '';
    const now = Date.now();
    const diffMs = now - parseInt(timestamp, 10);
    if (diffMs <= 0) return '';

    const duration = moment.duration(diffMs);
    const days = Math.floor(duration.asDays());
    const hours = Math.floor(duration.asHours()) % 24;
    const minutes = Math.floor(duration.asMinutes()) % 60;
    const seconds = Math.floor(duration.asSeconds()) % 60;

    const parts = [];
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);
    if (seconds > 0) parts.push(`${seconds} sec${seconds !== 1 ? 's' : ''}`);

    return parts && parts.length > 0 ? parts.slice(0, 2).join(', ') + ' ago' : '';
  };

  const isBanned = nftStatus?.banned === true;
  const lastSeenText = (() => {
    if (statusLoading) return 'Loading...';
    if (!nftStatus) return 'Never';
    if (nftStatus.connectTime) return 'Now, currently connected';
    const ts = nftStatus.lastSeen || nftStatus.lastJobCompleteTime || nftStatus.lastSpeedAuditTime;
    const rel = ts ? calculateRelativeTime(String(ts)) : '';
    return rel || 'Never';
  })();

  // -----------------------------------------------
  // 1) "Verify you are Human" => Turnstile challenge
  // -----------------------------------------------
  const handleTurnstileVerify = () => {
    if (!window.turnstile) {
      setTurnstileError('Human verification could not load. Please refresh and try again.');
      return;
    }

    setTurnstileError(null);
    setTurnstileToken('');

    try {
      if (turnstileWidgetIdRef.current !== null) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render('#turnstile-widget', {
        sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
        theme: 'dark',
        callback: (token) => {
          setTurnstileError(null);
          setTurnstileToken(token);
        },
        'expired-callback': () => {
          setTurnstileError('Human verification expired. Please verify again.');
          setTurnstileToken('');
        },
        'timeout-callback': () => {
          setTurnstileError('Human verification timed out. Please verify again.');
          setTurnstileToken('');
        },
        'error-callback': () => {
          setTurnstileError('Human verification failed to complete. Please try again.');
          setTurnstileToken('');
        }
      });
    } catch (error) {
      console.error('Unable to render Turnstile widget:', error);
      turnstileWidgetIdRef.current = null;
      setTurnstileError('Human verification could not start. Please refresh and try again.');
    }
  };

  // 2) The actual "Mint" button calls your existing `handleMint`
  //    which calls your server's getMintSignature with { userAddress, tokenId, turnstileToken }
  const handleMintClick = async () => {
    if (!turnstileToken) {
      alert('Please solve the Turnstile challenge first.');
      return;
    }
    // Then do the actual mint steps
    await handleMint();
  };

  // Mirror useNFTGallery's defensive token-id ceiling. In normal navigation an
  // unminted token above this ceiling is never added to the gallery.
  const isAboveMintLimit = !!selectedNFT && Number(selectedNFT.tokenId) > mintLimit;

  // Handle wallet signature verification for scanned NFTs
  const handleSignatureVerification = async () => {
    if (!window.ethereum) {
      setVerificationError('No wallet detected. Please connect your wallet.');
      return;
    }

    // Check if sogniUsername exists before proceeding
    if (!sogniUsername) {
      setVerificationError('Sogni username is required for NFT binding. Please log in to Sogni.');
      return;
    }

    setIsVerifying(true);
    setVerificationError(null);

    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length === 0) {
        throw new Error('No accounts found. Please connect your wallet.');
      }

      const address = accounts[0];
      const timestamp = Date.now(); // Use milliseconds timestamp to match API expectations
      const tokenIds = [selectedNFT.tokenId];
      const message = `I hereby permanently bind NFT(s) ${tokenIds.join(',')} from contract ${selectedNFT.contractAddress} to Sogni username "${sogniUsername}". This binding is irreversible. Timestamp: ${timestamp}`;
      
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, address],
      });

      if (signature) {
        // Call the API to persist the verification
        const baseUrl = getApiBaseUrl();
        if (!baseUrl) {
          console.warn('API base URL not configured, verification will not persist');
          setIsVerified(true);
          return;
        }

        try {
          const response = await axios.post(
            `${baseUrl}/nfts/third-party`,
            {
              contractAddress: selectedNFT.contractAddress,
              tokenIds: tokenIds,
              sogniUsername: sogniUsername || null,
              owner: selectedNFT.owner || null,
              signature: signature,
              message: message,
              timestamp: timestamp
            }
          );

          if (response.data.success) {
            console.log('Verification persisted:', response.data);
            setIsVerified(true);
            setVerificationPersisted(true);
          } else {
            throw new Error(response.data.error || 'Verification failed');
          }
        } catch (apiError) {
          console.error('API verification error:', apiError);
          
          // Extract error message from response
          let errorMessage = 'An error occurred: ';
          if (apiError.response && apiError.response.data && apiError.response.data.error) {
            errorMessage += apiError.response.data.error;
          } else if (apiError.message) {
            errorMessage += apiError.message;
          } else {
            errorMessage += 'Failed to persist verification';
          }
          
          // Display the error to the user
          setVerificationError(errorMessage);
          
          // Don't mark as verified if the API call failed
          setIsVerified(false);
        }
      }
    } catch (error) {
      console.error('Signature verification error:', error);
      setVerificationError(error.message || 'Failed to verify ownership');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ color: isBanned ? '#ff4d4f' : '#39ff14' }}>
          {(() => {
            const baseTitle = selectedNFT?.isScannedNFT 
              ? (selectedNFT?.name || `NFT #${selectedNFT?.tokenId}`)
              : `Sogni Prospect Worker #${selectedNFT?.tokenId}`;
            return isBanned ? `${baseTitle} (BANNED)` : baseTitle;
          })()}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* NFT image or video */}
        <div className="text-center">
          {selectedNFT.isScannedNFT && selectedNFT.animationUrl ? (
            <>
              {/* Show placeholder/thumbnail while video loads */}
              {(videoLoading || videoError) && selectedNFT.image && (
                <div style={{ position: 'relative', display: videoLoading && !videoError ? 'block' : 'none' }}>
                  <img
                    src={selectedNFT.image}
                    alt={selectedNFT.name || `NFT #${selectedNFT.tokenId}`}
                    className={imageClassName}
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '10px' }}
                  />
                  {videoLoading && !videoError && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: '50%',
                      width: '60px',
                      height: '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Spinner animation="border" variant="light" />
                    </div>
                  )}
                </div>
              )}
              {/* Error state - show image with error message */}
              {videoError && (
                <div>
                  <img
                    src={selectedNFT.image || PROSPECT_IMG}
                    alt={selectedNFT.name || `NFT #${selectedNFT.tokenId}`}
                    className={imageClassName}
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '10px' }}
                  />
                  <Alert variant="warning" style={{ marginTop: '10px' }}>
                    Unable to load video. Showing image preview instead.
                  </Alert>
                </div>
              )}
              {/* Video element - hidden until ready */}
              {!videoError && (
                <video
                  src={selectedNFT.animationUrl}
                  alt={selectedNFT.name || `NFT #${selectedNFT.tokenId}`}
                  className={imageClassName}
                  style={{ 
                    maxWidth: '100%', 
                    height: 'auto', 
                    borderRadius: '10px',
                    display: videoLoading ? 'none' : 'block'
                  }}
                  controls
                  autoPlay
                  loop
                  muted
                  onLoadedData={() => {
                    console.log('Video loaded and ready to play');
                    setVideoLoading(false);
                  }}
                  onCanPlay={() => {
                    console.log('Video can play');
                    setVideoLoading(false);
                  }}
                  onError={(e) => {
                    console.error('Video load error:', e);
                    setVideoError(true);
                    setVideoLoading(false);
                  }}
                />
              )}
            </>
          ) : (
            <img
              src={
                blacklistImageUrl
                  ? blacklistImageUrl
                  : isLocked
                    ? `${getApiBaseUrl()}/nft-status-image/locked`
                    : (selectedNFT.isScannedNFT && selectedNFT.image ? selectedNFT.image : PROSPECT_IMG)
              }
              alt={selectedNFT.name || `Sogni Prospect Worker #${selectedNFT.tokenId}`}
              className={imageClassName}
              style={{ maxWidth: '100%', height: 'auto', borderRadius: '10px' }}
            />
          )}
        </div>

        {/* Basic info */}
        <div style={{ marginTop: '20px' }}>
          <strong>Token ID:</strong> {selectedNFT.tokenId}
          <br />
          {selectedNFT.chain && (
            <>
              <strong>Network:</strong> {selectedNFT.chain}
              <br />
            </>
          )}
          {selectedNFT.isScannedNFT && selectedNFT.description && (
            <>
              <strong>Description:</strong> {selectedNFT.description}
              <br />
            </>
          )}
          {!selectedNFT.isScannedNFT && (
            <>
              {isBlackListed && (
                <>
                  <strong style={{ color: '#ff4d4f' }}>⚠ STATUS: BLACKLISTED</strong>
                  <br />
                  <div style={{ fontSize: '0.9rem', color: '#ff6b6b', marginBottom: '10px' }}>
                    This NFT has been blacklisted due to repeated ToS violations and cannot be used for work.
                  </div>
                </>
              )}
              {isLocked && !isBlackListed && (
                <>
                  <strong style={{ color: '#ffaa00' }}>🔒 STATUS: LOCKED</strong>
                  <br />
                  <div style={{ fontSize: '0.9rem', color: '#ffcc44', marginBottom: '10px' }}>
                    This NFT is locked — it has been bridged to another chain. Transfers are disabled until it is bridged back.
                  </div>
                </>
              )}
              <strong>Reputation Score:</strong> {
                (() => {
                  if (!selectedNFT.minted) {
                    return 'blank slate';
                  }
                  if (isBlackListed) {
                    return 'BANNED';
                  }
                  const bannedCount = lifetimeStats?.bannedNFTWorker || 0;
                  if (isBanned || (typeof bannedCount === 'number' && bannedCount > 0)) {
                    return `Sus${bannedCount > 0 ? `, banned ${bannedCount} time${bannedCount === 1 ? '' : 's'}` : 'pect'}`;
                  }
                  return selectedNFT.reputation || 'Building reputation...';
                })()
              }
              <br />
              {(() => {
                const accepted = Number(lifetimeStats?.jobAcceptedWorker ?? NaN);
                const completed = Number(lifetimeStats?.jobCompleteWorker ?? NaN);
                if (!Number.isFinite(accepted) || !Number.isFinite(completed)) return null;
                const successRate = accepted > 0 ? (completed / accepted) * 100 : 0;
                const formattedCompleted = Math.round(completed).toLocaleString();
                const formattedRate = successRate.toLocaleString(undefined, { maximumFractionDigits: 2 });
                return (
                  <>
                    <strong>Jobs Done:</strong> {formattedCompleted} ({formattedRate}% success rate)
                    <br />
                  </>
                );
              })()}
            </>
          )}
          <strong>Owner:</strong>{' '}
          {selectedNFT.minted || selectedNFT.isScannedNFT ? getOwnerLabelFormatted() : 'Unminted'}
          <br />
          <strong>Last Seen:</strong> {lastSeenText}

          {selectedNFT.minted && selectedNFT?.tokenId && (
            <>
              {selectedNFT.chain === 'Etherlink' ? (
                <>
                  <br />
                  <a
                    href={`https://explorer.etherlink.com/token/${process.env.REACT_APP_ETHERLINK_NFT_CONTRACT}/instance/${selectedNFT.tokenId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#39ff14' }}
                  >
                    View on Etherlink Explorer
                  </a>
                </>
              ) : publicContract?.address ? (
                <>
                  <br />
                  <a
                    href={`https://opensea.io/item/base/${publicContract.address}/${selectedNFT.tokenId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#39ff14' }}
                  >
                    View on OpenSea
                  </a>
                  <br />
                  <a
                    href={`https://basescan.org/nft/${publicContract.address}/${selectedNFT.tokenId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#39ff14' }}
                  >
                    View on Base Explorer
                  </a>
                </>
              ) : null}
              <br />
              {/* "View API key" link */}
              <a
                href="#"
                style={{ color: '#39ff14' }}
                onClick={handleViewApiKey}
              >
                View API Key
              </a>
              <br />
              {/* "View Fast Worker Real-time Analytics" link */}
              <Link
                to={`/analytics?nftId=${selectedNFT.tokenId}&section=gpu`}
                style={{ color: '#39ff14' }}
              >
                View Fast Worker Real-time Analytics
              </Link>
              {showApiKeyPanel && (
                <div
                  style={{
                    border: '1px solid #444',
                    padding: '10px',
                    marginTop: '10px',
                    borderRadius: '5px',
                    backgroundColor: '#333'
                  }}
                >
                  {apiKeyError ? (
                    <Alert variant="danger">{apiKeyError}</Alert>
                  ) : (
                    <>
                      <p style={{ wordBreak: 'break-all' }}>
                        <strong>API Key:</strong> {apiKey}
                      </p>
                      <p style={{ fontSize: '0.85rem', color: '#aaa', marginBottom: '15px' }}>
                        Note that your API Key is unique to your Sogni Wallet Address and will be the same for all NFTs under the account. If you Reset it here, you should update all your Fast Workers with the new API Key.
                      </p>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => copyToClipboard(apiKey)}
                        style={{ marginRight: '10px' }}
                      >
                        Copy
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={handleResetApiKey}
                        style={{ marginRight: '10px' }}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowApiKeyPanel(false)}
                      >
                        Close
                      </Button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Transfer if minted and I'm the owner (external wallet OR Sogni session wallet) */}
        {selectedNFT.minted &&
          ownerAddressLc &&
          viewerAddressLc &&
          viewerAddressLc === ownerAddressLc && (
            <div style={{ marginTop: '30px' }}>
              <h4>Owner Controls</h4>
              <Form>
                <Form.Group controlId="transferAddress">
                  <Form.Label>
                    Transfer NFT to (address, ENS, or Sogni username):
                  </Form.Label>
                  <div style={{ fontSize: '0.9rem', marginBottom: '8px', color: '#aaa' }}>
                    You can move this NFT to a new wallet or a Sogni address if
                    you suspect any security concern with your current wallet.
                  </div>
                  <Form.Control
                    type="text"
                    placeholder="Enter recipient"
                    value={transferAddress}
                    onChange={(e) => setTransferAddress(e.target.value)}
                    style={{ backgroundColor: '#333', color: '#fff' }}
                  />
                </Form.Group>
                {shortTransferError && (
                  <Alert variant="danger" style={{ marginTop: '10px' }}>
                    {shortTransferError}
                  </Alert>
                )}
                {transferSuccess && (
                  <Alert variant="success" style={{ marginTop: '10px' }}>
                    {transferSuccess}
                  </Alert>
                )}
                <Button
                  variant="success"
                  onClick={handleTransferClick}
                  disabled={transferring || !transferAddress.trim()}
                  style={{ marginTop: '10px' }}
                >
                  {transferring ? (
                    <Spinner as="span" animation="border" size="sm" />
                  ) : (
                    'Transfer'
                  )}
                </Button>
              </Form>
            </div>
          )}

        {/* For scanned NFTs - show verification section */}
        {selectedNFT.isScannedNFT && (
          <div style={{ marginTop: '20px' }}>
            <hr />
            <h5>Ownership Verification</h5>
            {verificationError && (
              <Alert variant="danger" dismissible onClose={() => setVerificationError(null)}>
                {verificationError}
              </Alert>
            )}
            <Button
              variant={isVerified ? "success" : "warning"}
              onClick={handleSignatureVerification}
              disabled={isVerifying || isVerified || checkingVerification}
              style={{ 
                backgroundColor: isVerified ? '#39ff14' : undefined,
                borderColor: isVerified ? '#39ff14' : undefined,
                color: isVerified ? '#000' : undefined
              }}
            >
              {checkingVerification ? (
                <Spinner as="span" animation="border" size="sm" />
              ) : isVerifying ? (
                <Spinner as="span" animation="border" size="sm" />
              ) : isAlreadyVerified ? (
                '✓ Already Verified'
              ) : isVerified ? (
                '✓ Verified'
              ) : (
                'Verify Ownership'
              )}
            </Button>
            {isVerified && (
              <p style={{ marginTop: '10px', color: '#39ff14' }}>
                {isAlreadyVerified ? 'Perks already applied!' : 'Ownership verified, perks applied!'}
              </p>
            )}
          </div>
        )}

        {/* Above the mint limit: show a notice instead of the mint UI */}
        {!selectedNFT.minted && !selectedNFT.isScannedNFT && isAboveMintLimit && (
          <Alert variant="info" style={{ marginTop: '30px' }}>
            Token ID {selectedNFT.tokenId} is above the current mint limit of {mintLimit}. Minting is not yet available for this NFT.
          </Alert>
        )}

        {/* MINT section if not minted, not scanned, and within the mint limit */}
        {!selectedNFT.minted && !selectedNFT.isScannedNFT && !isAboveMintLimit && (
          <div style={{ marginTop: '30px' }}>
            {shortMintError && (
              <Alert variant="danger" style={{ marginTop: '10px' }}>
                {shortMintError}
              </Alert>
            )}
            {mintSuccess && (
              <Alert variant="success" style={{ marginTop: '10px' }}>
                {mintSuccess}
              </Alert>
            )}

            <Alert variant="success" style={{ marginTop: '10px' }}>
              Minting is open to everyone. You can mint multiple NFTs directly to
              the same Sogni account, with no per-account limit while supply is
              available. Each GPU worker needs its own NFT. No extra accounts or
              transfers are needed.
            </Alert>

            {/* The Turnstile widget container */}
            <div
              id="turnstile-widget"
              style={{
                marginBottom: '10px',
              }}
            ></div>
            {turnstileError && (
              <Alert variant="warning" style={{ marginTop: '10px' }}>
                {turnstileError}
              </Alert>
            )}

            {/* 3 possible states for the user:
                A) Not logged in => "Login to Mint"
                B) Logged in but no turnstile token => "Verify you are Human"
                C) Logged in + turnstile token => "Mint for 0.0 ETH"
            */}

            {/* A) Not logged in => show "Login to Mint" */}
            {!isSogniLoggedIn && (
              <Button
                variant="success"
                style={{ marginRight: '10px' }}
                onClick={() => {
                  console.log('Login to Mint clicked!');
                  if (!handleSogniLogin) {
                    alert('No handleSogniLogin function provided!');
                    return;
                  }
                  handleSogniLogin();
                }}
              >
                Login to Mint
              </Button>
            )}

            {/* B) isSogniLoggedIn but no turnstile => "Verify you are Human" */}
            {isSogniLoggedIn && !turnstileToken && (
              <Button
                variant="warning"
                style={{ marginRight: '10px' }}
                disabled={minting}
                onClick={() => {
                  console.log('Verify you are Human clicked');
                  handleTurnstileVerify();
                }}
              >
                {turnstileWidgetIdRef.current === null
                  ? 'Verify you are Human'
                  : 'Try Verification Again'}
              </Button>
            )}

            {/* C) isSogniLoggedIn + have turnstileToken => "Mint" */}
            {isSogniLoggedIn && turnstileToken && (
              <>
                <Button
                  variant="success"
                  onClick={handleMintClick}
                  disabled={minting}
                  style={{ backgroundColor: '#39ff14', borderColor: '#39ff14' }}
                >
                  {minting ? (
                    <Spinner as="span" animation="border" size="sm" />
                  ) : (
                    `Mint for ${mintPrice} Base ETH`
                  )}
                </Button>
              </>
            )}
            {!selectedNFT.isScannedNFT && (
              <Alert variant="warning" style={{ marginTop: '20px' }}>
                <strong>Buying one or more Worker NFTs</strong>
                <ol className="mt-2">
                  <li>Sign in with the Sogni account that will own your NFTs.</li>
                  <li>
                    Fund that account&apos;s Sogni wallet with ETH on the{' '}
                    <strong>Base network</strong>. Allow{' '}
                    <strong>{mintPrice} ETH per NFT</strong>, plus gas for each mint.
                  </li>
                  <li>
                    Select an available NFT, verify you are human, and mint it.
                    Repeat for each additional NFT using the same account.
                  </li>
                </ol>
                <p>
                  You can fund your wallet once for several mints. For 3 NFTs,
                  the mint fees total{' '}
                  <strong>
                    {ethers.utils.formatEther(ethers.utils.parseEther(mintPrice).mul(3))} ETH
                  </strong>, plus gas for three mint transactions.
                </p>

The Prospect Worker NFT lets you <strong>test the waters</strong> as a Sogni Supernet Fast Worker and begin earning SOGNI from day one. As demand grows, the floor price of the Prospect NFT may rise above {mintPrice} ETH.<br /><br />

Once you've completed <strong>200,000 render units</strong> as a Prospect, you'll be <strong>eligible to upgrade</strong> this NFT to a Fast Worker license. Your Prospect NFT is fully transferable and sellable, but the Fast Worker NFT becomes soul-bound, cementing your personal reputation on chain.
More details{' '}
                <a
                  href="https://docs.sogni.ai/sogni-fast-worker/about-sogni-fast-worker"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'black' }}
                >
                  here
                </a>.
              </Alert>
            )}
          </div>
        )}

        {/* If minted but viewer is not the owner or not logged in */}
        {selectedNFT.minted && (
          !viewerAddressLc ? (
            <Alert variant="warning" style={{ marginTop: '20px' }}>
              Please log in to Sogni or connect your wallet to access owner controls.
            </Alert>
          ) : viewerAddressLc !== ownerAddressLc ? (
            <Alert variant="warning" style={{ marginTop: '20px' }}>
              You are not the owner of this NFT.
            </Alert>
          ) : null
        )}
      </Modal.Body>
    </Modal>
  );
};

export default NFTDetailModal;
